import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import cx from 'classnames';

import { Course, CourseValidationError } from "src/types/coaching/courses";

import { useDispatch } from "react-redux";
import { addAssetToCourse, createAssessment, editCourse, fetchCourse, fetchDraftCourse, publishCourse, rearrangeEntitiesInCourse, removeEntityFromCourse, updateCourse, updateValidationErrorsForCourse, validateCourse } from "src/redux/actions/coaching/courses";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AddAssetsDialog } from "src/components/asset/add";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { animated, Spring, useTransition } from "@react-spring/web";

import { getCoursePreferences, prepareCourseRequest, updateCoursePreferences, validateCourseRequest } from "../helper";
import { nanoid } from "nanoid";
import { CourseContext, CourseContextProps } from "../context";
import { CourseMenuBar } from "../components/menu";
import { AssessmentCard, LessonCard, PlaceholderCard } from "../components/cards";
import { EditCourseContent } from "./content";
import Button from "src/components/button";

import styles from "./structure.module.scss";
import { PublishView } from "..";
import { AddLearnersDialog } from "../components/learners/add";
import { fetchCourseLearners } from "src/redux/actions/coaching/learners";
import LinesEllipsis from "react-lines-ellipsis";
import Tooltip from "src/components/tooltip";

import userRoleService from "src/services/userRoles.service";
import PermissionDenined from "src/components/permissionDenied";
import Text from "src/components/text";

export const EditCourseStructurePage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const id = params.id;

  const contentIndex = useMemo(() => {
    return parseInt(searchParams.get('index') || '0');
  }, [searchParams]);

  const persistedCourse: Course = useSelector((state: any) =>
    _.get(state.courses, `course[${id}].item`) as Course,
  );

  const loading = useSelector((state: any) => {
    return _.get(state.courses, `course[${id}].loading`);
  });

  const course: Course = useSelector((state: any) => {
    return _.get(state.courses, `edit.item`) as Course;
  });

  const courses = useSelector((state: any) => {
    return _.get(state, 'courses');
  });

  const activeCourseContent = useMemo(() => {
    return _.get(course, `contents[${contentIndex}].uuid`);
  }, [course, contentIndex]);

  const { loadingLearners, learners, currentPage, nextPage, hasMoreItems } = useSelector((state: any) => {
    const learnersState = _.get(state, 'courses.learners');
    const loadingLearners = _.get(learnersState, 'loading');
    const pagination = _.get(learnersState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");
    const currentPage = _.get(pagination, "currentPage", 0);

    const learners = (_.get(learnersState, 'items') || []);

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loadingLearners: loadingLearners,
      learners: learners,
      currentPage: currentPage,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems
    };
  }, _.isEqual);

  const [showAddAssets, setShowAddAssets] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [showAddLearner, setShowAddLearner] = useState(false);

  const [menuState, setMenuState] = useState({
    primaryAction: null,
    secondaryAction: null
  });

  const [courseContext, setCourseContext] = useState<CourseContextProps>({
    id: id,
    courseContent: activeCourseContent,
    preferences: getCoursePreferences(),
    updatePreferences: (preferences) => { },
    gotoError: (error?: any) => { },
  });

  const editTransitions = useTransition(!isPublished, {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  })

  useEffect(() => {
    if (id) {
      dispatch(fetchDraftCourse(id));
    }

    setCourseContext((prev) => {
      return {
        ...prev,
        preferences: getCoursePreferences(),
        gotoError: handleGoToError,
        updatePreferences: handleCoursePreferencesChange
      }
    });
  }, [id]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        courseContent: activeCourseContent
      }
    });
  }, [activeCourseContent]);

  useEffect(() => {
    if (persistedCourse && persistedCourse.id !== course?.id) {
      dispatch(editCourse(persistedCourse));
      dispatch(validateCourse(persistedCourse.id!));
    }
  }, [persistedCourse, course]);

  useEffect(() => {
    if (menuState.primaryAction === 'save') {
      handleSave();
      setMenuState((prev) => {
        return {
          ...prev,
          primaryAction: null,
        }
      });
    } else if (menuState.primaryAction === 'publish') {
      handlePublish();
      setMenuState((prev) => {
        return {
          ...prev,
          primaryAction: null,
        }
      });
    }

  }, [menuState]);

  useEffect(() => {
    if (courseContext.validationError) {
      const type = courseContext.validationError.type;
      const uuid = courseContext.validationError.uuid;

      switch (type) {
        case 'course': {
          navigate(`/coaching/manage/courses/draft/${id}`);
          break;
        }

        case 'assessment': {
          const index = _.findIndex(course.contents, (content) => content.uuid === uuid);
          navigate(`/coaching/manage/courses/draft/${id}/contents?index=${index}`);
          break;
        }

        case 'lesson': {
          const index = _.findIndex(course.contents, (content) => content.uuid === uuid);
          navigate(`/coaching/manage/courses/draft/${id}/contents?index=${index}`);
          courseContext.gotoError(undefined);
          break;
        }

        default: {
          console.log('Unknown validation error', courseContext.validationError);
        }
      }
    }
  }, [courseContext.validationError]);

  useEffect(() => {
    if (courses.publish?.published) {
      setIsPublished(true);
    }
  }, [courses.publish])

  const handleAddLessonClicked = () => {
    setShowAddAssets(true);
  }

  const handleFinishAddLessons = (assets: any) => {
    if (_.size(assets) > 0) {
      assets.filter(asset => {
        return (course.contents || []).filter((content) => content.entityType === 'lesson')
        .findIndex((content) => content.asset?.id === asset.id) === -1;
      }).forEach((asset: any) => {
        dispatch(addAssetToCourse({
          id: course!.id!,
          asset: asset,
          order: -1,
        }));
      });
    }
  };

  const handleCloseAddLessons = () => {
    setShowAddAssets(false);
  }

  const handleAddAssessmentClicked = () => {
    dispatch(createAssessment({
      course: course.id!,
      name: 'New Assessment',
      order: -1,
      questions: [{
        uuid: nanoid(6),
        name: 'Question 1',
        type: 'multiple_choice',
        answer: 'option-1',
        order: 0,
        options: [{
          uuid: 'option-1',
          name: 'Option 1',
          order: 1,
        }, {
          uuid: 'option-2',
          name: 'Option 2',
          order: 2,
        }, {
          uuid: 'option-3',
          name: 'Option 3',
          order: 3,
        }, {
          uuid: 'option-4',
          name: 'Option 4',
          order: 4,
        }],
      }, {
        uuid: nanoid(6),
        name: 'Question 2',
        type: 'true_false',
        answer: "yes",
        order: 1,
        options: [{
          uuid: 'yes',
          name: 'Yes',
          order: 0,
        }, {
          uuid: 'no',
          name: 'No',
          order: 1,
        }],
      }, {
        uuid: nanoid(6),
        name: 'Question 3',
        type: 'text',
        answer: 'A concise answer to the question that will be evaluated using ai',
        order: 2,
      }]
    }));
  }

  const handleSave = () => {
    const validationErrors = validateCourseRequest(course);

    if (_.size(validationErrors) > 0) {
      dispatch(updateValidationErrorsForCourse({
        course: course.id!,
        errors: validationErrors,
      }));

      return;
    }

    const request = prepareCourseRequest(persistedCourse, course);

    dispatch(updateCourse({
      id: course.id!,
      request: request
    }));
  }

  const handlePublish = () => {
    const validationErrors = validateCourseRequest(course);

    if (_.size(validationErrors) > 0) {
      dispatch(updateValidationErrorsForCourse({
        course: course.id!,
        errors: validationErrors,
      }));

      return;
    }

    dispatch(publishCourse(course.id!));
  }

  const handleMenuStateChange = (state) => {
    setMenuState(state);
  }

  const handleCoursePreferencesChange = (preferences) => {
    setCourseContext((prev) => {
      return {
        ...prev,
        preferences: preferences
      }
    });

    updateCoursePreferences(preferences);
  }

  const handleGoToError = (error?: CourseValidationError) => {
    setCourseContext((prev) => {
      return {
        ...prev,
        validationError: error
      }
    });
  }

  const handleMoreLearners = () => {
    if (nextPage) {
      const request = {
        course: id,
        status: "ALL",
        page: nextPage,
        limit: 10,
      };
      dispatch(fetchCourseLearners(request));
    }
  }

  if(!userRoleService.canCreateCourse()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  if ((loading && course?.id != id) || !course) {
    return (
      <div className={cx(styles.loadingContainer, styles.content)}>
        <div className={styles.left}>
          <div className={cx(styles.header, styles.placeholder)}>
            <button className={styles.backButton} onClick={() => navigate(`/coaching/manage/courses/draft/${course.id}`)}>
              <i className={'paperflite-left-arrow'}></i>
              <div className={styles.courseName}>course details</div>
            </button>
            <div className={cx(styles.block, styles.title)} style={{ height: "10px" }}></div>
          </div>

          <div className={styles.placeholderCards}>
            {[...Array(3)].map((_, index) => (
              <PlaceholderCard key={index} index={index} />
            ))}
          </div>
        </div>
        <div className={styles.right}>
          <div className={styles.placeholderContent}>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CourseContext.Provider value={courseContext}>
      {editTransitions((style, item) => {
        return (
          item && (
            <animated.div style={style}>
              <div className={styles.editContainer}>
                <CourseMenuBar
                  course={course}
                  menuState={menuState}
                  onMenuStateChange={handleMenuStateChange}
                />
                <div className={styles.left}>
                  <CourseStructure
                    course={course}
                    contentIndex={contentIndex}
                    onAddLessonClicked={handleAddLessonClicked}
                    onAddAssessmentClicked={handleAddAssessmentClicked}
                  />
                </div>

                <div className={styles.right}>
                  <EditCourseContent
                    course={course}
                    contentIndex={contentIndex}
                    onAddLessonClicked={handleAddLessonClicked}
                    onAddAssessmentClicked={handleAddAssessmentClicked}
                  />
                </div>

                <AddAssetsDialog
                  show={showAddAssets}
                  selectableEntityTypes={["asset"]}
                  source={{
                    type: "course",
                    entity: course,
                  }}
                  onFinish={handleFinishAddLessons}
                  onClose={handleCloseAddLessons}
                />
              </div>
            </animated.div>
          )
        );
      })}

      <PublishView
        isOpen={isPublished}
        onClose={() => {
          setIsPublished(false);
        }}
        learners={learners || []}
        handleAssignLearners={() => setShowAddLearner(true)}
        loading={loadingLearners}
        hasMoreItems={hasMoreItems}
        handleLoadMoreLearners={handleMoreLearners}
      />

      <AddLearnersDialog
        show={showAddLearner}
        course={course}
        onFinish={() => setShowAddLearner(false)}
        onClose={() => setShowAddLearner(false)}
      />
    </CourseContext.Provider>
  );
}

interface CourseStructureProps {
  course: Course;
  contentIndex: number;
  onAddLessonClicked: () => void;
  onAddAssessmentClicked: () => void;
}

export const CourseStructure: React.FC<CourseStructureProps> = ({ course, contentIndex, onAddLessonClicked, onAddAssessmentClicked }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const contents = _.get(course, 'contents') || [];

  const [showTooltip, setShowTooltip] = useState(false);

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8
    }
  });

  const sensors = useSensors(
    pointerSensor
  );

  const getIndexForLesson = (lesson) => {
    const lessons = contents.filter((content) => content.entityType === 'lesson');
    return _.findIndex(lessons, (content) => content.uuid === lesson);
  }

  const handleContentClicked = (index) => {
    navigate(`/coaching/manage/courses/draft/${course.id}/contents?index=${index}`);
  }

  const handleRemove = (index) => {
    dispatch(removeEntityFromCourse({
      id: course.id!,
      index: index
    }));
  }

  const handleDragFinish = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const source = active.data.current.entity;
      const target = over.data.current.entity;

      const targetIndex = _.findIndex(contents, (content) => content.entityType === target.entityType && content.uuid === target.uuid);
      navigate(`/coaching/manage/courses/draft/${course.id}/contents?index=${targetIndex}`, { replace: true });

      dispatch(rearrangeEntitiesInCourse({
        id: course.id!,
        source: {
          id: source.uuid,
          entityType: source.entityType
        },
        target: {
          id: target.uuid,
          entityType: target.entityType
        }
      }));
    }
  }

  return (
    <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragOver={handleDragFinish}>
      <div className={styles.content}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={() => navigate(`/coaching/manage/courses/draft/${course.id}`)}>
            <i className={'paperflite-left-arrow'}></i>
            <Text className={styles.courseName} text={course.name} lines={1} tooltip={{placement: 'bottom'}} />
          </button>

          <h2 className={styles.title}>Course Structure</h2>
          {_.size(contents) > 0 && (
            <div className={styles.actions}>
              <Button
                variant="gradient"
                className={styles.action}
                onClick={onAddLessonClicked}
                text="Add Lesson"
                icon="paperflite-add"
              ></Button>
              <Button
                variant="gradient"
                className={styles.action}
                onClick={onAddAssessmentClicked}
                text="Add Assessment"
                icon="paperflite-add"
              ></Button>
            </div>
          )}
        </div>

        <CourseContent
          contents={contents}
          contentIndex={contentIndex}
          onAddLessonClicked={onAddLessonClicked}
          onAddAssessmentClicked={onAddAssessmentClicked}
          handleContentClicked={handleContentClicked}
          handleRemove={handleRemove}
          getIndexForLesson={getIndexForLesson}
        />
      </div>
    </DndContext>
  );
}



interface CourseContentProps {
  contents: any[];
  contentIndex: number;
  onAddLessonClicked: () => void;
  onAddAssessmentClicked: () => void;
  handleContentClicked: (index: number) => void;
  handleRemove: (index: number) => void;
  getIndexForLesson: (lesson: string) => number;
}

const CourseContent: React.FC<CourseContentProps> = ({ contents, contentIndex, onAddLessonClicked, onAddAssessmentClicked, handleContentClicked, handleRemove, getIndexForLesson }) => {

  if (_.size(contents) === 0) {
    return (
      <div className={styles.placeholderCards}>
        {[...Array(3)].map((_, index) => (
          <PlaceholderCard key={index} index={index} />
        ))}
      </div>);
  }

  return (
    <div className={styles.body}>
      <SortableContext id={"course_structure"} items={contents.map(content => content.uuid)} strategy={verticalListSortingStrategy}>
        {contents.map((content, index) => {
          if (content.entityType === 'lesson') {
            const lessonIndex = getIndexForLesson(content.uuid);
            return <LessonCard key={content.uuid}
              lesson={content}
              lessonIndex={lessonIndex}
              index={index}
              active={index === contentIndex}
              onClick={handleContentClicked}
              onRemove={handleRemove}
            />
          } else if (content.entityType === 'assessment') {
            return <AssessmentCard key={content.uuid}
              index={index}
              assessment={content}
              active={index === contentIndex}
              onClick={handleContentClicked}
              onRemove={handleRemove}
            />
          }
        })}
      </SortableContext>
    </div>
  );
}